import React, { useState, useEffect, useMemo } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import styles from './CreateRoutineModal.module.scss';
import { useRoutines } from '../../hooks/queries/useRoutines';
import { useProfiles } from '../../hooks/queries/useProfiles';
import { PredefinedRoutine, PredefinedRoutineTask, RoutineWithTasks } from '../../services/supabase/routines';
import { Task } from '../../types';
import { 
    generateTasksFromTemplate, 
    timeToMinutes, 
    minutesToTime, 
    addMinutes,
    ProposedTask 
} from '../../utils/routineHelpers';
import { TaskPicker } from '../TimelineEditor/TaskPicker';
import {
    getSuggestedEmojis,
    EMOJI_CATEGORIES,
    getEmojiCategories
} from '../../utils/emojiSuggestions';
import { SortableTaskItem } from './SortableTaskItem';
import { tokens } from '../../styles/tokens';
import { shadeForIndex } from '../../utils/colorUtils';

interface ThemeColors {
    textColor: string;
    secondaryText: string;
    accentColor: string;
    appBackground?: string;
    cardBackground?: string;
}

interface CreateRoutineModalProps {
    onClose: () => void;
    existingRoutines?: Task[]; // For overlap detection
    editingRoutineId?: string | null; // If provided, we're in edit mode
    profileRoutines?: RoutineWithTasks[]; // For loading routine data in edit mode
    profileId?: string | null; // Profile ID for fetching routines if needed
    themeColors?: ThemeColors; // Theme-aware styling
}

type Step = 1 | 2 | 3;
type TimingAnchor = 'fixed_start' | 'fixed_end';

export const CreateRoutineModal: React.FC<CreateRoutineModalProps> = ({ 
    onClose, 
    existingRoutines = [],
    editingRoutineId,
    profileRoutines: propProfileRoutines = [],
    profileId,
    themeColors
}) => {
    // Fetch routines using the hook as a fallback if props are empty
    const { predefinedRoutines, createRoutine, updateRoutine, profileRoutines: hookProfileRoutines, routinesLoading } = useRoutines(profileId || undefined);
    const { profiles } = useProfiles();
    
    // Theme-aware styles
    const modalStyle: React.CSSProperties = themeColors ? {
        background: themeColors.appBackground,
        color: themeColors.textColor,
    } : {};
    
    const textStyle: React.CSSProperties = themeColors ? { color: themeColors.textColor } : {};
    
    // Use prop routines if available, otherwise use hook routines
    const profileRoutines = propProfileRoutines.length > 0 ? propProfileRoutines : hookProfileRoutines;
    
    // Determine if we're in edit mode
    const isEditMode = !!editingRoutineId;
    
    // Find the editing routine - use useMemo to avoid recalculating on every render
    const editingRoutine = useMemo(() => {
        if (!isEditMode || profileRoutines.length === 0) return null;
        return profileRoutines.find(r => r.id === editingRoutineId) || null;
    }, [isEditMode, editingRoutineId, profileRoutines]);
    
    const [step, setStep] = useState<Step>(isEditMode ? 2 : 1); // Skip template selection in edit mode
    const [selectedTemplate, setSelectedTemplate] = useState<PredefinedRoutine | null>(null);
    
    // Step 2 State - Now asks "When do we want to be done?"
    const [timingAnchor, setTimingAnchor] = useState<TimingAnchor>('fixed_start');
    const [anchorTime, setAnchorTime] = useState('07:00'); // Either start or end time based on anchor
    const [name, setName] = useState('');
    const [startTime, setStartTime] = useState('07:00');
    const [endTime, setEndTime] = useState('08:00');
    const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri default
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');
    const [selectedChildIds, setSelectedChildIds] = useState<string[]>([]);
    const [overlapWarning, setOverlapWarning] = useState<string | null>(null);
    const [routineColor, setRoutineColor] = useState('#5DADE2'); // Sky Blue

    // Step 3 State
    const [tasks, setTasks] = useState<(ProposedTask & { id: string })[]>([]);
    const [showTaskPicker, setShowTaskPicker] = useState(false);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [quickAddName, setQuickAddName] = useState('');
    const [quickAddIcon, setQuickAddIcon] = useState('⭐');
    const [quickAddDuration, setQuickAddDuration] = useState(15);
    const [quickAddColor, setQuickAddColor] = useState('#5DADE2'); // Sky Blue
    const [showAllEmojis, setShowAllEmojis] = useState(false);
    const [selectedEmojiCategory, setSelectedEmojiCategory] = useState<keyof typeof EMOJI_CATEGORIES>('favorites');
    
    const [isInitialized, setIsInitialized] = useState(false);

    // DnD sensors - configure with activation constraints for better touch support
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Minimum distance before drag starts
            }
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200, // Small delay to distinguish from scroll
                tolerance: 5, // Allow small movements during delay
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    // Initialize form with editing routine data
    useEffect(() => {
        // Only initialize once we have the editing routine data
        if (!isEditMode || !editingRoutine || isInitialized) return;
        
        setName(editingRoutine.name);
        setRoutineColor(editingRoutine.color || '#5DADE2');
        setSelectedDays(editingRoutine.days_of_week || [1, 2, 3, 4, 5]);
        setStartDate(editingRoutine.start_date || new Date().toISOString().split('T')[0]);
        setEndDate(editingRoutine.end_date || '');
        
        // Set timing
        if (editingRoutine.start_time) {
            const time = editingRoutine.start_time.slice(0, 5);
            setAnchorTime(time);
            setStartTime(time);
        }
        if (editingRoutine.end_time) {
            setEndTime(editingRoutine.end_time.slice(0, 5));
        }
        
        // Set profile (child) ID from the routine
        if (editingRoutine.profile_id) {
            setSelectedChildIds([editingRoutine.profile_id]);
        }
        
        // Convert routine tasks to proposed tasks
        if (editingRoutine.tasks && editingRoutine.tasks.length > 0) {
            const sortedTasks = [...editingRoutine.tasks].sort((a, b) => 
                (a.order_index || 0) - (b.order_index || 0)
            );
            const proposedTasks = sortedTasks.map((t, i) => {
                const startMins = timeToMinutes(t.start_time.slice(0, 5));
                const endMins = timeToMinutes(t.end_time.slice(0, 5));
                let duration = endMins - startMins;
                if (duration <= 0) duration = 15;
                
                return {
                    id: t.id,
                    label: t.label,
                    icon: t.icon || '⭐',
                    color: t.color || '#5DADE2',
                    duration,
                    orderIndex: i
                };
            });
            setTasks(proposedTasks);
        }
        
        setIsInitialized(true);
    }, [isEditMode, editingRoutine, isInitialized]);

    // Initialize child selection from profileId prop or first profile
    useEffect(() => {
        if (selectedChildIds.length === 0) {
            if (profileId) {
                // Use the passed profileId (active profile from Dashboard)
                setSelectedChildIds([profileId]);
            } else if (profiles.length === 1) {
                setSelectedChildIds([profiles[0].id]);
            }
        }
    }, [profiles, profileId, selectedChildIds]);

    // Calculate total routine duration
    const totalTaskDuration = useMemo(() => {
        return tasks.reduce((sum, t) => sum + t.duration, 0);
    }, [tasks]);

    // Recalculate start/end times based on timing anchor when tasks change
    const calculatedTimes = useMemo(() => {
        if (timingAnchor === 'fixed_start') {
            const endTimeCalc = addMinutes(anchorTime, totalTaskDuration || 60);
            return { start: anchorTime, end: endTimeCalc };
        } else {
            // Fixed end - calculate start by subtracting duration
            const totalDur = totalTaskDuration || 60;
            const endMins = timeToMinutes(anchorTime);
            let startMins = endMins - totalDur;
            if (startMins < 0) startMins += 24 * 60; // Handle crossing midnight
            return { start: minutesToTime(startMins), end: anchorTime };
        }
    }, [timingAnchor, anchorTime, totalTaskDuration]);

    // Check for overlap with existing routines
    const checkOverlap = useMemo(() => {
        if (!existingRoutines || existingRoutines.length === 0) return null;
        
        const newStart = timeToMinutes(calculatedTimes.start);
        const newEnd = timeToMinutes(calculatedTimes.end);
        
        // Tasks no longer carry their own day-of-week. Conflict detection now
        // operates purely on time overlap; per-day intersection happens at the
        // routine level once we have routine-aware conflict APIs.
        for (const existing of existingRoutines) {
            const existingStart = timeToMinutes(existing.start);
            const existingEnd = timeToMinutes(existing.end);
            const overlap = !(newEnd <= existingStart || newStart >= existingEnd);
            if (overlap) {
                return `This routine overlaps with "${existing.label}"`;
            }
        }
        return null;
    }, [existingRoutines, calculatedTimes]);

    // Update start/end times when anchor changes
    useEffect(() => {
        setStartTime(calculatedTimes.start);
        setEndTime(calculatedTimes.end);
        setOverlapWarning(checkOverlap);
    }, [calculatedTimes, checkOverlap]);

    // Calculate individual task times based on order and anchor
    const taskTimes = useMemo(() => {
        const times: Array<{ startTime: string; endTime: string }> = [];
        let currentTime = calculatedTimes.start;
        
        for (const task of tasks) {
            const taskStart = currentTime;
            const taskEnd = addMinutes(currentTime, task.duration);
            times.push({ startTime: taskStart, endTime: taskEnd });
            currentTime = taskEnd;
        }
        
        return times;
    }, [tasks, calculatedTimes.start]);

    const handleTemplateSelect = (template: PredefinedRoutine | null) => {
        setSelectedTemplate(template);
        if (template) {
            setName(template.name);
        } else {
            setName('My Routine');
        }
        setStep(2);
    };

    const handleGenerateTasks = () => {
        if (selectedChildIds.length === 0) {
            alert('Please select at least one child.');
            return;
        }

        const startMin = timeToMinutes(startTime);
        const endMin = timeToMinutes(endTime);
        let duration = endMin - startMin;
        if (duration < 0) duration += 24 * 60; // Cross midnight
        
        if (selectedTemplate) {
            // Fetch tasks for template - they are nested in predefinedRoutines
            const templateWithTasks = selectedTemplate as PredefinedRoutine & { tasks: PredefinedRoutineTask[] };
            const generated = generateTasksFromTemplate(templateWithTasks, duration);
            // Add unique IDs for drag-and-drop
            const tasksWithIds = generated.map((t, i) => ({ ...t, id: `task-${i}-${Date.now()}` }));
            setTasks(tasksWithIds);
        } else {
            setTasks([]);
        }
        setStep(3);
    };

    // Handle drag-and-drop reorder
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        
        if (over && active.id !== over.id) {
            setTasks((items) => {
                const oldIndex = items.findIndex(item => item.id === active.id);
                const newIndex = items.findIndex(item => item.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleTaskDurationChange = (index: number, change: number) => {
        setTasks(prev => {
            const updated = [...prev];
            const newDuration = updated[index].duration + change;
            
            if (newDuration < 1) return prev; // Min 1 min
            
            updated[index] = { ...updated[index], duration: newDuration };
            return updated;
        });
    };

    const handleDeleteTask = (index: number) => {
        setTasks(tasks.filter((_, i) => i !== index));
    };

    const handleTaskEdit = (index: number, updates: Partial<ProposedTask>) => {
        setTasks(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], ...updates };
            return updated;
        });
    };

    const handleAddTask = (taskTemplate: any) => {
        const newTask: ProposedTask & { id: string } = {
            id: `task-${tasks.length}-${Date.now()}`,
            label: taskTemplate.label,
            icon: taskTemplate.icon,
            color: taskTemplate.color,
            duration: 15, // Default new task duration
            orderIndex: tasks.length
        };
        
        // Simply add the task - no redistribution, duration is additive
        setTasks([...tasks, newTask]);
        setShowTaskPicker(false);
    };
    
    const handleQuickAddSubmit = () => {
        if (!quickAddName.trim()) return;
        
        const newTask: ProposedTask & { id: string } = {
            id: `task-${tasks.length}-${Date.now()}`,
            label: quickAddName.trim(),
            icon: quickAddIcon,
            color: quickAddColor,
            duration: quickAddDuration,
            orderIndex: tasks.length
        };
        
        // Simply add the task - no redistribution, duration is additive
        setTasks([...tasks, newTask]);
        
        // Reset quick add form
        setQuickAddName('');
        setQuickAddIcon('⭐');
        setQuickAddDuration(15);
        setQuickAddColor('#5DADE2');
        setShowQuickAdd(false);
    };
    
    // Playful Focus theme colors — kept distinct so only one swatch reads as
    // selected (duplicates previously highlighted two swatches for one choice).
    const QUICK_COLORS = ['#5DADE2', '#9B8ED8', '#E09F3E', '#32B197', '#D65A4A', '#A51870', '#F2C94C', '#5C6DB0'];
    const ROUTINE_COLORS = QUICK_COLORS;
    
    // Card colors for routine type selection (Playful Focus theme)
    const CARD_COLORS: Record<string, string> = {
        'custom': '#1C1C1C',      // Ink Black
        'morning': '#F2C94C',     // Sun Yellow
        'bedtime': '#5C6DB0',     // Purple Blue
        'after_school': '#32B197', // Teal
        'dinner': '#E09F3E',      // Warm Orange
        'weekend_morning': '#9B8ED8', // Lavender Purple
        'cleanup': '#D65A4A',     // Soft Red
        'homework': '#A51870',    // Magenta
        'music': '#32B197',       // Teal
        'leaving': '#5DADE2'      // Sky Blue
    };

    const handleSave = async () => {
        // Validate required fields
        if (!name.trim()) {
            alert('Please enter a routine name.');
            return;
        }
        
        if (selectedChildIds.length === 0) {
            alert('Please select at least one child.');
            return;
        }
        
        if (tasks.length === 0 && !isEditMode) {
            alert('Please add at least one task to the routine.');
            return;
        }
        
        // Check for overlap before saving
        if (overlapWarning) {
            if (!window.confirm(`${overlapWarning}\n\nDo you want to save anyway?`)) {
                return;
            }
        }

        try {
            if (isEditMode && editingRoutineId) {
                console.log('[CreateRoutineModal] Updating routine:', editingRoutineId);
                // Update existing routine (only routine settings, not tasks for now)
                await updateRoutine({
                    routineId: editingRoutineId,
                    updates: {
                        name,
                        startTime: calculatedTimes.start,
                        endTime: calculatedTimes.end,
                        daysOfWeek: selectedDays,
                        startDate,
                        endDate: endDate || '9999-12-31',
                        color: routineColor
                    }
                });
                console.log('[CreateRoutineModal] Routine updated successfully');
            } else {
                // Calculate start times for each task based on timing anchor
                let currentTime = calculatedTimes.start;
                const tasksWithTimes = tasks.map((t, i) => {
                    const start = currentTime;
                    const end = addMinutes(currentTime, t.duration);
                    currentTime = end;
                    return {
                        label: t.label,
                        icon: t.icon,
                        // Each task automatically gets a shade of the routine color,
                        // so they stay visually bundled but remain distinguishable.
                        color: shadeForIndex(routineColor, i),
                        start_time: start,
                        end_time: end,
                        order_index: i,
                    };
                });

                console.log('[CreateRoutineModal] Creating routine with tasks:', {
                    name,
                    childIds: selectedChildIds,
                    type: selectedTemplate?.type || 'custom',
                    startTime: calculatedTimes.start,
                    endTime: calculatedTimes.end,
                    daysOfWeek: selectedDays,
                    tasksCount: tasksWithTimes.length,
                    tasks: tasksWithTimes
                });

                await createRoutine({
                    childIds: selectedChildIds,
                    name,
                    type: selectedTemplate?.type || 'custom',
                    startTime: calculatedTimes.start,
                    endTime: calculatedTimes.end,
                    daysOfWeek: selectedDays,
                    startDate,
                    endDate: endDate || '9999-12-31', // Far future if null
                    color: routineColor,
                    tasks: tasksWithTimes
                });
                
                console.log('[CreateRoutineModal] Routine created successfully');
            }
            
            onClose();
        } catch (e) {
            console.error('[CreateRoutineModal] Failed to save routine:', e);
            alert(`Failed to save routine: ${e instanceof Error ? e.message : 'Unknown error'}`);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <h2 style={textStyle}>
                        {isEditMode ? (
                            step === 2 ? 'Edit Routine Settings' : 'Edit Tasks'
                        ) : (
                            <>
                                {step === 1 && 'Select Routine Type'}
                                {step === 2 && 'Routine Settings'}
                                {step === 3 && 'Review Tasks'}
                            </>
                        )}
                    </h2>
                    <button className={styles.closeButton} onClick={onClose}>×</button>
                </div>

                <div className={styles.content}>
                    {/* Show loading state in edit mode while routine data loads */}
                    {isEditMode && !editingRoutine && (
                        <div style={{ padding: '40px', textAlign: 'center', color: tokens.charcoal }}>
                            {routinesLoading ? (
                                <>Loading routine data...</>
                            ) : profileRoutines.length === 0 ? (
                                <>No routines found. Please try again.</>
                            ) : (
                                <>Routine not found (ID: {editingRoutineId}). It may have been deleted.</>
                            )}
                        </div>
                    )}
                    
                    {step === 1 && !isEditMode && (
                        <div className={styles.step1Container}>
                            <div className={styles.minuQuestion}>
                                <p className={styles.questionText}>What routine do you want to create?</p>
                            </div>
                            <div className={styles.grid}>
                                <div
                                    className={`${styles.coloredCard} ${selectedTemplate === null ? styles.selected : ''}`}
                                    style={{ backgroundColor: CARD_COLORS['custom'] }}
                                    onClick={() => handleTemplateSelect(null)}
                                >
                                    <div className={styles.cardName}>Create my own</div>
                                </div>
                                {predefinedRoutines.filter(t => t.type !== 'custom').map(t => (
                                    <div
                                        key={t.id}
                                        className={`${styles.coloredCard} ${selectedTemplate?.id === t.id ? styles.selected : ''}`}
                                        style={{ backgroundColor: CARD_COLORS[t.type] || tokens.skyBlue }}
                                        onClick={() => handleTemplateSelect(t)}
                                    >
                                        {/* Sentence-case: capital first letter, rest lowercase */}
                                        <div className={styles.cardName}>
                                            {t.name.charAt(0).toUpperCase() + t.name.slice(1).toLowerCase()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 2 && (!isEditMode || editingRoutine) && (
                        <div>
                            {/* Timing Anchor with Minu Character */}
                            <div className={styles.timingSection}>
                                <div className={styles.toggleContainer}>
                                    <div className={styles.pillToggle}>
                                        <button
                                            type="button"
                                            className={`${styles.pillBtn} ${timingAnchor === 'fixed_start' ? styles.active : ''}`}
                                            onClick={() => setTimingAnchor('fixed_start')}
                                        >
                                            fix start
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.pillBtn} ${timingAnchor === 'fixed_end' ? styles.active : ''}`}
                                            onClick={() => setTimingAnchor('fixed_end')}
                                        >
                                            fix end
                                        </button>
                                    </div>
                                </div>
                                
                                <div className={styles.timingQuestionBubble}>
                                    <p className={styles.bubbleText}>
                                        {timingAnchor === 'fixed_start' 
                                            ? 'when do we want to start the routine?'
                                            : 'when do we want do be done?'}
                                    </p>
                                    <div className={styles.timeInputWrapper}>
                                        <input
                                            type="time"
                                            value={anchorTime}
                                            onChange={e => setAnchorTime(e.target.value)}
                                            className={styles.bubbleTimeInput}
                                            placeholder="hh:mm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="routine-name">Routine Name</label>
                                <input id="routine-name" type="text" value={name} onChange={e => setName(e.target.value)} />
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label>Which Days?</label>
                                <div className={styles.daysSelector}>
                                    {['S','M','T','W','T','F','S'].map((d, i) => (
                                        <button 
                                            key={i}
                                            type="button"
                                            className={selectedDays.includes(i) ? styles.active : ''}
                                            onClick={() => {
                                                if (selectedDays.includes(i)) {
                                                    setSelectedDays(selectedDays.filter(d => d !== i));
                                                } else {
                                                    setSelectedDays([...selectedDays, i]);
                                                }
                                            }}
                                        >
                                            {d}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            <div className={styles.row}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="routine-start-date">Start Date</label>
                                    <input id="routine-start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="routine-end-date">End Date (Optional)</label>
                                    <input id="routine-end-date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                                </div>
                            </div>

                            {profiles.length > 0 && (
                                <div className={styles.formGroup}>
                                    <label>For Children</label>
                                    <div className={styles.childSelection}>
                                        {profiles.map(p => (
                                            <label key={p.id} className={styles.childLabel}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedChildIds.includes(p.id)}
                                                    onChange={e => {
                                                        if (e.target.checked) {
                                                            setSelectedChildIds([...selectedChildIds, p.id]);
                                                        } else {
                                                            setSelectedChildIds(selectedChildIds.filter(id => id !== p.id));
                                                        }
                                                    }}
                                                />
                                                <span>{p.display_name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Routine Color */}
                            <div className={styles.formGroup}>
                                <label>Routine Color</label>
                                <div className={styles.colorSelection}>
                                    {ROUTINE_COLORS.map(color => (
                                        <button
                                            key={color}
                                            type="button"
                                            className={`${styles.routineColorBtn} ${routineColor === color ? styles.selected : ''}`}
                                            style={{ background: color }}
                                            onClick={() => setRoutineColor(color)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            {/* Timing Summary */}
                            <div className={styles.timingSummary}>
                                <div className={styles.timingInfo}>
                                    {timingAnchor === 'fixed_start' ? (
                                        <>
                                            <span className={styles.timeAnchorBadge}>🏁 Start: {startTime}</span>
                                            <span className={styles.timeArrow}>→</span>
                                            <span className={styles.timeCalculated}>Done by: {endTime}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className={styles.timeCalculated}>Start: {startTime}</span>
                                            <span className={styles.timeArrow}>→</span>
                                            <span className={styles.timeAnchorBadge}>🎯 Done by: {endTime}</span>
                                        </>
                                    )}
                                    <span className={styles.totalDuration}>({totalTaskDuration} min)</span>
                                </div>
                                {overlapWarning && (
                                    <div className={styles.overlapWarning}>
                                        ⚠️ {overlapWarning}
                                    </div>
                                )}
                            </div>

                            {/* Time Bar - uses individual task colors */}
                            <div className={styles.timeBar}>
                                {tasks.map((t, i) => (
                                    <div
                                        key={t.id}
                                        className={styles.segment}
                                        style={{
                                            flex: t.duration,
                                            background: shadeForIndex(routineColor, i),
                                            opacity: 0.9
                                        }}
                                        title={`${t.label} (${t.duration}m)`}
                                    />
                                ))}
                            </div>

                            <p className={styles.dragHint}>📌 Drag tasks to reorder</p>

                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={tasks.map(t => t.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className={styles.taskList}>
                                        {tasks.map((task, index) => (
                                            <SortableTaskItem
                                                key={task.id}
                                                task={task}
                                                index={index}
                                                startTime={taskTimes[index]?.startTime}
                                                endTime={taskTimes[index]?.endTime}
                                                routineColor={routineColor}
                                                onDurationChange={handleTaskDurationChange}
                                                onDelete={handleDeleteTask}
                                                onEdit={handleTaskEdit}
                                                availableIcons={EMOJI_CATEGORIES.favorites.concat(
                                                    EMOJI_CATEGORIES.hygiene,
                                                    EMOJI_CATEGORIES.clothing,
                                                    EMOJI_CATEGORIES.food.slice(0, 8),
                                                    EMOJI_CATEGORIES.school.slice(0, 8),
                                                    EMOJI_CATEGORIES.activities.slice(0, 8),
                                                    EMOJI_CATEGORIES.sleep
                                                ).filter((v, i, a) => a.indexOf(v) === i)} // Remove duplicates
                                                availableColors={QUICK_COLORS}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>

                            <div className={styles.taskList}>
                                {/* Quick Add Section */}
                                {showQuickAdd ? (
                                    <div className={styles.quickAddForm}>
                                        {/* Task Name Input */}
                                        <input
                                            type="text"
                                            placeholder="Task name..."
                                            value={quickAddName}
                                            onChange={(e) => {
                                                setQuickAddName(e.target.value);
                                                // Auto-suggest first icon based on name
                                                const suggested = getSuggestedEmojis(e.target.value, 1);
                                                if (suggested.length > 0 && quickAddIcon === '⭐') {
                                                    setQuickAddIcon(suggested[0]);
                                                }
                                            }}
                                            className={styles.quickAddInput}
                                            autoFocus
                                        />
                                        
                                        {/* Suggested Icons (based on task name) */}
                                        <div className={styles.quickAddRow}>
                                            <span className={styles.iconSectionLabel}>
                                                {quickAddName.trim() ? '✨ Suggested:' : '⭐ Choose icon:'}
                                            </span>
                                        </div>
                                        <div className={styles.quickAddRow}>
                                            <div className={styles.quickAddIconPicker}>
                                                {getSuggestedEmojis(quickAddName, 8).map(icon => (
                                                    <button
                                                        key={icon}
                                                        type="button"
                                                        className={`${styles.iconBtn} ${quickAddIcon === icon ? styles.selected : ''}`}
                                                        onClick={() => setQuickAddIcon(icon)}
                                                    >
                                                        {icon}
                                                    </button>
                                                ))}
                                                <button
                                                    type="button"
                                                    className={`${styles.iconBtn} ${styles.moreBtn}`}
                                                    onClick={() => setShowAllEmojis(!showAllEmojis)}
                                                    title="Browse all icons"
                                                >
                                                    {showAllEmojis ? '▲' : '▼'}
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* All Emojis by Category (expandable) */}
                                        {showAllEmojis && (
                                            <div className={styles.emojiCategorySection}>
                                                <div className={styles.categoryTabs}>
                                                    {getEmojiCategories().map(cat => (
                                                        <button
                                                            key={cat}
                                                            type="button"
                                                            className={`${styles.categoryTab} ${selectedEmojiCategory === cat ? styles.active : ''}`}
                                                            onClick={() => setSelectedEmojiCategory(cat)}
                                                        >
                                                            {EMOJI_CATEGORIES[cat][0]} {cat}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className={styles.categoryEmojiGrid}>
                                                    {EMOJI_CATEGORIES[selectedEmojiCategory].map(icon => (
                                                        <button
                                                            key={icon}
                                                            type="button"
                                                            className={`${styles.iconBtn} ${quickAddIcon === icon ? styles.selected : ''}`}
                                                            onClick={() => {
                                                                setQuickAddIcon(icon);
                                                                setShowAllEmojis(false);
                                                            }}
                                                        >
                                                            {icon}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className={styles.quickAddRow}>
                                            <label className={styles.quickAddLabel}>Duration:</label>
                                            <div className={styles.durationControl}>
                                                <button type="button" onClick={() => setQuickAddDuration(d => Math.max(5, d - 5))}>-</button>
                                                <span>{quickAddDuration}m</span>
                                                <button type="button" onClick={() => setQuickAddDuration(d => d + 5)}>+</button>
                                            </div>
                                        </div>
                                        <div className={styles.quickAddRow}>
                                            <div className={styles.quickAddColors}>
                                                {QUICK_COLORS.map(color => (
                                                    <button
                                                        key={color}
                                                        type="button"
                                                        className={`${styles.colorBtn} ${quickAddColor === color ? styles.selected : ''}`}
                                                        style={{ background: color }}
                                                        onClick={() => setQuickAddColor(color)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <div className={styles.quickAddActions}>
                                            <button 
                                                type="button"
                                                className={styles.secondaryBtn} 
                                                onClick={() => {
                                                    setShowQuickAdd(false);
                                                    setShowAllEmojis(false);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button 
                                                type="button"
                                                className={styles.primaryBtn} 
                                                onClick={handleQuickAddSubmit}
                                                disabled={!quickAddName.trim()}
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.addTaskButtons}>
                                        <button 
                                            className={`${styles.secondaryBtn} ${styles.addTaskBtn}`}
                                            onClick={() => setShowQuickAdd(true)}
                                        >
                                            ✏️ Quick Add
                                        </button>
                                        <button 
                                            className={`${styles.secondaryBtn} ${styles.addTaskBtn}`}
                                            onClick={() => setShowTaskPicker(true)}
                                        >
                                            📚 From Library
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.footer}>
                    {step > 1 && !isEditMode && (
                        <button className={styles.secondaryBtn} onClick={() => setStep((s) => (s - 1) as Step)}>
                            Back
                        </button>
                    )}
                    {isEditMode && step === 3 && (
                        <button className={styles.secondaryBtn} onClick={() => setStep(2)}>
                            Back to Settings
                        </button>
                    )}
                    <div className={styles.footerSpacer} />
                    {isEditMode ? (
                        // Edit mode: show Save and optionally Edit Tasks
                        step === 2 ? (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button 
                                    className={styles.secondaryBtn} 
                                    onClick={() => setStep(3)}
                                >
                                    Edit Tasks
                                </button>
                                <button className={styles.primaryBtn} onClick={handleSave}>
                                    Update Routine
                                </button>
                            </div>
                        ) : (
                            <button className={styles.primaryBtn} onClick={handleSave}>
                                Update Routine
                            </button>
                        )
                    ) : (
                        // Create mode. Step 1 has no primary button — picking a routine
                        // type card advances automatically, so a separate "continue"
                        // button would be redundant.
                        step === 1 ? null : step === 2 ? (
                            <button className={styles.primaryBtn} onClick={handleGenerateTasks}>
                                Generate Tasks
                            </button>
                        ) : (
                            <button className={styles.primaryBtn} onClick={handleSave}>
                                Save Routine
                            </button>
                        )
                    )}
                </div>
            </div>
            
            {showTaskPicker && (
                <TaskPicker 
                    onSelect={handleAddTask}
                    onClose={() => setShowTaskPicker(false)}
                />
            )}
        </div>
    );
};
